import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import PromotionsNearby from '../components/PromotionsNearby';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, MapPin, Crown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SearchPage = () => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    specialization: '',
    location: '',
    minRating: ''
  });

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    try {
      const params = {};
      if (filters.specialization) params.specialization = filters.specialization;
      if (filters.location) params.location = filters.location;
      if (filters.minRating) params.min_rating = filters.minRating;

      const response = await api.get('/api/professionals', { params });
      setProfessionals(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei professionisti');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchProfessionals();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-12 text-center">
          <h1 className="font-heading font-semibold text-4xl sm:text-5xl text-foreground mb-4">
            Trova il Professionista Giusto
          </h1>
          <p className="text-lg text-foreground/70">Oltre {professionals.length} professionisti verificati disponibili</p>
        </div>

        {/* Filters */}
        <Card className="p-6 mb-8 neumorphism rounded-2xl" data-testid="search-filters">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Select value={filters.specialization || 'all'} onValueChange={(val) => setFilters({ ...filters, specialization: val === 'all' ? '' : val })}>
                <SelectTrigger data-testid="specialization-filter">
                  <SelectValue placeholder="Specializzazione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="Assistenza Infermieristica Domiciliare">Infermieristica</SelectItem>
                  <SelectItem value="Fisioterapia Domiciliare">Fisioterapia</SelectItem>
                  <SelectItem value="Supporto Psicologico">Psicologia</SelectItem>
                  <SelectItem value="Assistenza OSS/OSA">OSS/OSA</SelectItem>
                  <SelectItem value="Badante Convivente">Badante</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Input
                placeholder="Località"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                data-testid="location-filter"
              />
            </div>

            <div>
              <Select value={filters.minRating || 'all'} onValueChange={(val) => setFilters({ ...filters, minRating: val === 'all' ? '' : val })}>
                <SelectTrigger data-testid="rating-filter">
                  <SelectValue placeholder="Rating minimo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="4">4+ stelle</SelectItem>
                  <SelectItem value="4.5">4.5+ stelle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleSearch} className="rounded-full" data-testid="search-btn">
              Cerca
            </Button>
          </div>
        </Card>

        {/* Promozioni attive nella zona cercata */}
        <div className="mb-10">
          <PromotionsNearby location={filters.location} title="Promozioni nella tua zona" />
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : professionals.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-foreground/70">Nessun professionista trovato. Prova a modificare i filtri.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="professionals-grid">
            {professionals.map((prof) => (
              <Card
                key={prof.professional_id}
                className={`neumorphism rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-200 relative ${prof.is_premium ? 'ring-2 ring-accent' : ''}`}
                data-testid={`professional-card-${prof.professional_id}`}
              >
                {prof.is_premium && (
                  <div className="absolute top-3 right-3 z-10 bg-accent text-white px-3 py-1 rounded-full flex items-center gap-1 shadow-md" data-testid={`premium-badge-${prof.professional_id}`}>
                    <Crown size={14} weight="fill" />
                    <span className="text-xs font-medium">PREMIUM</span>
                  </div>
                )}
                <div className="aspect-video bg-muted relative">
                  {prof.picture ? (
                    <img src={prof.picture} alt={prof.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                      <span className="text-5xl">🥼</span>
                    </div>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="font-heading font-medium text-xl mb-2">
                      {prof.name}
                      {prof.is_center && (
                        <Badge className="ml-2 bg-primary/10 text-primary align-middle text-xs" data-testid={`center-badge-${prof.professional_id}`}>
                          CENTRO
                        </Badge>
                      )}
                    </h3>
                    {prof.is_center && (
                      <div className="text-xs text-foreground/70 mb-2" data-testid={`center-staff-${prof.professional_id}`}>
                        Centro di assistenza
                        {(prof.profession_codes || []).length > 0
                          ? ` · ${prof.profession_codes.map((c) => c.replace(/_/g, ' ')).join(', ')}`
                          : ''}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-foreground/70 mb-2">
                      <MapPin size={16} />
                      <span>{prof.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Star size={16} weight="fill" className="text-accent" />
                      <span className="font-medium">{prof.rating.toFixed(1)}</span>
                      <span className="text-foreground/60 text-sm">({prof.reviews_count} recensioni)</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {prof.specializations.slice(0, 2).map((spec, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-secondary text-secondary-foreground">
                        {spec}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium text-lg">{prof.hourly_rate}€/ora</span>
                    <Link to={`/professional/${prof.professional_id}`}>
                      <Button size="sm" className="rounded-full" data-testid={`view-profile-${prof.professional_id}`}>
                        Vedi Profilo
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;